// dependencies
import { GenerateProcess } from "redux-process";

import { MergeQueryParameters } from "../../../../actions/navigation.actions";
import { DisplaySuccess } from "../../../../actions/notifications.actions";
// actions
import { StateChange } from "../../../../actions/page.actions";

// selectors
import { selectActiveSection, selectRequestingNotifications, selectUserHasAdminDigest } from "./selectors";

// config
import config from "./config";
// ui
import ui from "./ui";

/**
 * Updates the query parameter for the active section. This will cascade down
 * to the page component and inform it which section to render.
 * @param {string} sectionName - name of the new active section
 */
export const updateActiveSection = (sectionName) => (dispatch) => {
	dispatch(MergeQueryParameters({ activeSection: sectionName }));
};

/**
 * Initialize the view by fetching notifications and then initilizing the UI
 * after the requests have finished.
 */
export const init = () => async (dispatch, getState) => {
	dispatch(StateChange(config.pageName, [], ui));

	const state = getState();

	// get the current active section. If null, then update the url to have an
	// active section
	const activeSection = selectActiveSection(state, null);

	if (!activeSection) {
		dispatch(updateActiveSection("asap-daily"));
	}

	const userHasAdminDigest = selectUserHasAdminDigest(state);

	// do all network io
	await Promise.all([
		dispatch(GenerateProcess("getWeeklyDigestSettings")),
		dispatch(GenerateProcess("getAsapAndDeferredNotification")),
		new Promise((resolve) => {
			if (userHasAdminDigest) {
				return resolve(dispatch(GenerateProcess("getAdminWeeklyDigestSettings")));
			}

			return resolve(null);
		}),
	]).catch(() => null);

	// after all network io is settled, we can say that the pag is initialized
	dispatch(StateChange(config.pageName, ["initialized"], true));

	// we display the user timezone in the time picking sections. This is not
	// an important piece of information so we can dispatch the request but we
	// do not need to await it
	dispatch(GenerateProcess("getUserSettings"));
};

/**
 * Teardown the view.
 */
export const teardown = () => () => {};

/**
 * Sends a PATCH to update the notification state of an asap and deferred
 * notification. It will also update a list of requests that are ongoing in
 * the page state.
 * @param {string} notification - the notification identifier
 * @param {string} type - the value to set for that notification
 */
export const sendAsapAndDeferredPatch = (notification, type) => async (dispatch, getState) => {
	// create a new list of outgoing requests
	const requestingList = selectRequestingNotifications(getState()).setIn([notification], type);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], requestingList));

	// create the request payload
	const payload = {
		notificationSettings: [{ name: notification, type }],
	};

	// send request
	try {
		await dispatch(GenerateProcess("setAsapAndDeferredNotification", { payload }));
	} catch (ermahgerd) {
		// pass
	}

	// remove item from requesting list
	const newRequestingList = selectRequestingNotifications(getState()).delete(notification);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], newRequestingList));
};

/**
 * Sends a PATCH to update the notification state of a weekly digest
 * notification. It will also update a list of requests that are ongoing in the
 * page state.
 * @param {string} notification - the notification identifier
 * @param {string} type - the value to set for that notification
 */
export const sendDigestPatch = (notification, type) => async (dispatch, getState) => {
	// create a new list of outgoing requests
	const requestingList = selectRequestingNotifications(getState()).setIn([notification], type);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], requestingList));

	// create the request payload
	const payload = {
		notificationSettings: [{ name: notification, type }],
	};

	// send request
	try {
		await dispatch(GenerateProcess("setWeeklyDigestNotification", { payload }));
	} catch (ermahgerd) {
		//
	}

	// remove item from requesting list
	const newRequestingList = selectRequestingNotifications(getState()).delete(notification);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], newRequestingList));
};

/**
 * Sends a PATCH to update the notification state of an admin weekly digest
 * notification. It will also update a list of requests that are ongoing in the
 * page state.
 * @param {string} notification - the notification identifier
 * @param {string} type - the value to set for that notification
 */
export const sendAdminDigestPatch = (notification, type) => async (dispatch, getState) => {
	// create a new list of outgoing requests
	const requestingList = selectRequestingNotifications(getState()).setIn([notification], type);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], requestingList));

	// create the request payload
	const payload = {
		notificationSettings: [{ name: notification, type }],
	};

	// send request
	try {
		await dispatch(GenerateProcess("setAdminWeeklyDigestNotification", { payload }));
	} catch (ermahgerd) {
		// pass
	}

	// remove item from requesting list
	const newRequestingList = selectRequestingNotifications(getState()).delete(notification);

	// update state
	dispatch(StateChange(config.pageName, ["awaitingRequests"], newRequestingList));
};

/**
 * Sends a PATCH with the notification times.
 * @param {object[]} times - an array of objects with the time, no mre than two
 *
 * @example
 *
 *  sendDeferredTimingPatch([{ hour: 16 }, { hour: 12 }])
 */
export const sendDeferredTimingPatch = (times) => async (dispatch) => {
	// update state
	dispatch(StateChange(config.pageName, ["awaitingTimingRequest"], true));

	// create the request payload
	const payload = {
		frequencySettings: times,
	};

	// send request
	try {
		const request = await dispatch(GenerateProcess("setAsapAndDeferredNotification", { payload }));

		if (request.succeeded) {
			dispatch(DisplaySuccess("Updated daily notification settings"));
		}
	} catch (ermahgerd) {
		// pass
	}

	// update state
	dispatch(StateChange(config.pageName, ["awaitingTimingRequest"], false));
};

/**
 * Updates the timing configuration of the weekly digest.
 * @param {string} dayOfWeek - day of the week
 * @param {number} hour - 0-23 hour
 */
export const sendWeeklyDigestTimingPatch = (dayOfWeek, hour) => async (dispatch) => {
	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], true));

	// create the request payload
	const payload = {
		deliverySettings: {
			hour,
			dayOfWeek,
		},
	};

	// send request
	try {
		const request = await dispatch(GenerateProcess("setWeeklyDigestNotification", { payload }));

		if (request.succeeded) {
			dispatch(DisplaySuccess("Updated weekly digest notification settings"));
		}
	} catch (ermahgerd) {
		// pass
	}

	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], false));
};

/**
 * Updates the timing configuration of the weekly digest.
 * @param {string} dayOfWeek - day of the week
 * @param {number} hour - 0-23 hour
 */
export const sendWeeklyAdminDigestTimingPatch = (dayOfWeek, hour) => async (dispatch) => {
	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], true));

	// create the request payload
	const payload = {
		deliverySettings: {
			hour,
			dayOfWeek,
		},
	};

	// send request
	try {
		const request = await dispatch(GenerateProcess("setAdminWeeklyDigestNotification", { payload }));

		if (request.succeeded) {
			dispatch(DisplaySuccess("Updated weekly admin digest notification settings"));
		}
	} catch (ermahgerd) {
		// pass
	}

	// update state
	dispatch(StateChange(config.pageName, ["awaitingDigestTimingRequest"], false));
};
